import type { AutoAdvance, RequirementChangedPayload } from '@onefootprint/idv2';
import type { FootprintReasonCode, OnboardingSessionConfig } from '@onefootprint/request-types';
import type { IdDocOutcome, ObConfigAuth, OverallOutcome } from '@onefootprint/types';
import type { DoneArgs } from '@/idv/components/identify';
import type { DeviceInfo } from '../../hooks';
import type { BootstrapData } from '../../types';
import type { AuthTokenPayload, DeviceResponseJsonPayload } from './utils/custom-listener';

export enum ComponentsSdkTypes {
  WEB = 'web',
  MOBILE = 'mobile',
}

export type ComponentsSdkContext = {
  onRelayFromComponents: (cb: () => void) => () => void;
  relayToComponents: (response: { authToken: string; vaultingToken: string }) => void;
  componentsSdkType: ComponentsSdkTypes;
  skipRelayToComponents?: boolean;
};

export enum ConfigRequestFailureReason {
  invalidConfig = 'invalidConfig',
  sessionExpired = 'sessionExpired',
  other = 'other',
}

/** These constant properties are often passed around to other idv machines */
export type CommonIdvContext = {
  device: DeviceInfo;
  authToken: string;
  isTransfer?: boolean;
  componentsSdkContext?: ComponentsSdkContext;
  isInIframe?: boolean;
  backContext?: {
    canGoBack: boolean;
    onBack: () => void;
  };
};

export type CompletePayload = {
  validationToken?: string;
  authToken?: string; // auth token used for onboarding flow
  deviceResponseJson?: string; // from passkey registration
  /** True when idv2 rendered the playbook's authored completion screen (Complete node) */
  hasCompletionScreen?: boolean;
};

export type MachineContext = {
  // Inputs
  config?: OnboardingSessionConfig;
  device?: DeviceInfo;
  authToken?: string;
  bootstrapData: BootstrapData;
  isTransfer?: boolean;
  componentsSdkContext?: ComponentsSdkContext;
  isInIframe?: boolean;
  obConfigAuth?: ObConfigAuth;
  validationToken?: string;
  showLogo?: boolean;
  idDocOutcome?: IdDocOutcome;
  sandboxId?: string;
  overallOutcome?: OverallOutcome;
  fixtureRiskSignals?: FootprintReasonCode[];
  deviceResponseJson?: string;
  onClose?: () => void;
  onComplete?: (payload: CompletePayload) => void; // Generated
  autoAdvance?: AutoAdvance;
  onBackButtonVisibilityChange?: (canGoBack: boolean) => void;
  onRequirementChange?: (payload: RequirementChangedPayload) => void;
  onExternalBack?: (cb: () => void) => () => void;
  retries: number;
};

export type MachineEvents =
  | {
      type: 'initContextUpdated';
      payload: {
        config?: OnboardingSessionConfig;
        device?: DeviceInfo;
      };
    }
  | {
      type: 'configRequestFailed';
      payload: {
        reason: ConfigRequestFailureReason;
      };
    }
  | {
      type: 'sandboxOutcomeSubmitted';
      payload: {
        idDocOutcome?: IdDocOutcome;
        sandboxId?: string;
        overallOutcome?: OverallOutcome;
        fixtureRiskSignals?: FootprintReasonCode[];
      };
    }
  | {
      type: 'identifyCompleted';
      payload: DoneArgs;
    }
  | {
      type: 'onboardingCompleted';
      payload: {
        validationToken?: string;
      };
    }
  | {
      type: 'reset';
    }
  | {
      type: 'expireSession';
    }
  | AuthTokenPayload
  | DeviceResponseJsonPayload;
